<template>
  <VSection large label="transactions.technical_information_headline">
    <div class="row">
      <div class="col-l-6 col-m-12">
        <VSection label="transactions.transaction_description_headline" level="5">
          <VDescriptionList>
            <VEntry :data="transactionDetails" field="transactionId" name="transactions.overview_transaction_id_column_heading" />
            <VEntryDate :data="transactionDetails" field="start" name="transactions.transactionStart" />
            <VEntry :data="transactionDetails" field="stateDescription" name="transactions.state" />
            <VEntry :data="transactionDetails" field="finalState" name="transactions.isFinalState" boolean />
            <VEntry :data="transactionDetails" field="flagBundleUsage" name="transactions.bundleUsed" boolean />
            <VEntry :data="transactionDetails" field="transactionClass" name="transactions.transactionClass" />
            <VEntry :data="transactionDetails" field="type" name="transactions.transactionType" />
            <VEntry :data="transactionDetails" field="subscriptionDataChargeType" name="transactions.chargingType" />
            <VEntry :data="transactionDetails" field="origin" name="transactions.source" />
          </VDescriptionList>
        </VSection>
      </div>

      <div v-if="hasAdditionExternalElements" id="transctionAdditionalRequestElements" class="col-l-6 col-m-12">
        <VSection label="transactions.additional_description_elements_headline" level="5">
          <VueGoodTable
            style-class="table table-hover"
            :columns="tableColumnsAdditionalDescriptionElements"
            :rows="getDetailsAdditionalDescriptionElementsRows"
            :paginate="true"
            :rows-per-page-text="$t('common.tablePerPage')"
            :of-text="$t('common.tableOf')"
            :all-text="$t('common.tableAll')"
            :prev-text="$t('common.tablePrev')"
            :next-text="$t('common.tableNext')"
            :line-numbers="false"
          >
            <template #table-column="{ column }">
              <VTableHeader :columns="tableColumnsAdditionalDescriptionElements" :column="column" locale-namespace="transactions" />
            </template>
          </VueGoodTable>
        </VSection>
      </div>
    </div>

    <template v-if="isExpertMode">
      <VSection label="transactions.partner_contract_access_information_headline" level="5">
        <VDescriptionListDivided>
          <VGroup label="transactions.partner_contract_access_information_provider_headline">
            <VEntry :data="transactionDetails" field="providerContractId" name="transactions.partner_contract_access_information_partner_contract_id_label" />
            <VEntry
              :data="transactionDetails"
              field="providerContractNumber"
              name="transactions.partner_contract_access_information_partner_contract_number_label"
            />
          </VGroup>
          <VGroup label="transactions.partner_contract_access_information_broker_headline">
            <VEntry :data="transactionDetails" field="brokerContractId" name="transactions.partner_contract_access_information_broker_contract_id_label" />
            <VEntry :data="transactionDetails" field="brokerNumber" name="transactions.partner_contract_access_information_broker_contract_number_label" />
          </VGroup>
          <VGroup label="transactions.partner_contract_access_information_external_access_headline">
            <VEntry :data="transactionDetails" field="loginAccountId" name="transactions.partner_contract_access_information_login_account_label" />
            <VEntry :data="transactionDetails" field="loginAccountIpAndPort" name="transactions.partner_contract_access_information_ip_address_port_label" />
          </VGroup>
        </VDescriptionListDivided>
      </VSection>

      <div class="row">
        <div class="col-l-6 col-m-6">
          <VSection label="transactions.timestamps_headline" level="5">
            <VDescriptionList>
              <VEntryDate :data="transactionDetails" field="start" name="transactions.timestamps_transaction_start_label" />
              <VEntryDate :data="transactionDetails" field="lastModified" name="transactions.timestamps_last_change_label" />
              <VEntryDate :data="transactionDetails" field="subscriberPrepaidAccountingDate" name="transactions.timestamps_prepaid_booking_label" />
              <VEntryDate :data="transactionDetails" field="firstDeliveryTime" name="transactions.timestamps_first_delivery_label" />
              <VEntryDate :data="transactionDetails" field="lastDeliveryTime" name="transactions.timestamps_last_delivery_label" />
              <VEntryDate :data="transactionDetails" field="coverageEndTime" name="transactions.timestamps_expiry_label" />
              <VEntryDate :data="transactionDetails" field="terminationTime" name="transactions.transaction_end_label" />
            </VDescriptionList>
          </VSection>
        </div>
        <div v-if="transactionDetails && transactionDetails.transportContainers" class="col-l-6 col-m-6">
          <VSection label="transactions.transportContainerHeader" level="5">
            <VueGoodTable
              style-class="table table-hover"
              :columns="tableColumnsTransportContainer"
              :rows="getCalculatedTransportContainerRows"
              :paginate="true"
              :rows-per-page-text="$t('common.tablePerPage')"
              :of-text="$t('common.tableOf')"
              :all-text="$t('common.tableAll')"
              :prev-text="$t('common.tablePrev')"
              :next-text="$t('common.tableNext')"
              :line-numbers="false"
            >
              <template #table-column="{ column }">
                <VTableHeader :columns="tableColumnsTransportContainer" :column="column" locale-namespace="transactions" />
              </template>
            </VueGoodTable>
          </VSection>
        </div>
      </div>
    </template>
  </VSection>
</template>

<script>
import { mapGetters } from 'vuex';
import VSection from '@/components/Section';
import VDescriptionList from '@/components/DescriptionList';
import VDescriptionListDivided from '@/components/DescriptionList/Divided';
import VEntry from '@/components/DescriptionList/Entry';
import VEntryDate from '@/components/DescriptionList/EntryDate';
import VGroup from '@/components/DescriptionList/Group';
import { VueGoodTable } from 'vue-good-table';
import VTableHeader from '@/components/Table/Header';
import { formatValue } from '@/utils/date';

const tableColumnsTransportContainer = [
  {
    label: 'transactions.transport_container_item_name_column_heading',
    field: 'name',
    sortable: true,
    filterable: false,
    width: '40%',
  },
  {
    label: 'transactions.transport_container_item_description_column_heading',
    field: 'description',
    sortable: true,
    filterable: false,
    width: '40%',
  },
  {
    label: 'transactions.transport_container_item_value_column_heading',
    field: 'calculatedValue',
    sortable: true,
    width: '20%',
  },
];

const tableColumnsAdditionalDescriptionElements = [
  {
    label: 'transactions.additional_description_element_name_label',
    field: 'name',
    type: 'string',
    filterable: false,
    sortable: true,
    placeholder: ' ',
  },
  {
    label: 'transactions.additional_description_element_value_label',
    field: 'text',
    filterable: false,
    sortable: true,
    placeholder: ' ',
  },
];

export default {
  name: 'PaymentTransactionDetailsTechnicalDetails',

  components: {
    VueGoodTable,
    VSection,
    VDescriptionList,
    VDescriptionListDivided,
    VEntry,
    VEntryDate,
    VGroup,
    VTableHeader,
  },

  props: {
    // input data
    transactionDetails: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      tableColumnsTransportContainer,
      tableColumnsAdditionalDescriptionElements,
    };
  },

  computed: {
    ...mapGetters('transactions', ['isExpertMode']),

    hasTransactionDetails() {
      return !!this.transactionDetails;
    },

    hasAdditionExternalElements() {
      if (!this.transactionDetails) {
        return false;
      }
      return this.transactionDetails.additionalRequestDescriptions && this.transactionDetails.additionalRequestDescriptions.length > 0;
    },

    getDetailsAdditionalDescriptionElementsRows() {
      if (!this.transactionDetails) {
        return null;
      }
      if (!this.transactionDetails.additionalRequestDescriptions) {
        return null;
      }

      return this.transactionDetails.additionalRequestDescriptions;
    },

    getCalculatedTransportContainerRows() {
      if (!this.transactionDetails || !this.transactionDetails.transportContainers) {
        return [];
      }

      this.transactionDetails.transportContainers.forEach(element => {
        element.calculatedValue = formatValue(element.value);
      });

      return this.transactionDetails.transportContainers;
    },
  },
};
</script>
